import React, { useState, useEffect } from 'react';
import Card from '../../../../common/components/Card';
import { commonStyles } from '../../../../common/components/Layout/DashboardLayout';
import classNames from 'classnames';
import { useTheme } from '../../../../common/theme/themeContext';
import axios from 'axios';
import { KSK_API_CONFIG } from '../../config/apiConfig';
import SigfoxSeriesDeviceList from './SigfoxSeriesDeviceList';
import SigfoxDetailDevice from './SigfoxDetailDevice';

const SigfoxPage = () => {
  const { isDark } = useTheme();
  const [customersData, setCustomersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDevices, setSeriesDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [notes, setNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);

  // ローカルストレージから備考を読み込み
  useEffect(() => {
    const savedNotes = localStorage.getItem('sigfoxDeviceNotes');
    if (savedNotes) {
      setNotes(JSON.parse(savedNotes));
    }
  }, []);

  useEffect(() => {
    const fetchCustomersSeriesData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(KSK_API_CONFIG.CUSTOMERS_SERIES);
        if (response.data.status === 'success') {
          const data = response.data.data;
          setCustomersData(data.customers);
        } else {
          setError('データの取得に失敗しました');
        }
      } catch (error) {
        let errorMessage = 'API呼び出しに失敗しました';
        if (error.response) {
          errorMessage = `サーバーエラー: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
        } else if (error.request) {
          errorMessage = 'サーバーに接続できませんでした';
        } else {
          errorMessage = `エラー: ${error.message}`;
        }
        setError(errorMessage);
        setCustomersData([
          {
            customerCode: 'chowa',
            customerName: '調和工業株式会社',
            series: [
              { modelSeries: 'SR-60', deviceCount: 100 },
              { modelSeries: 'HV-300', deviceCount: 22 }
            ]
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomersSeriesData();
  }, []);

  const handleSeriesClick = async (customerCode, seriesName) => {
    try {
      setLoadingDevices(true);
      setError(null);
      const apiUrl = `${KSK_API_CONFIG.DEVICES_BY_SERIES}/?customer=${customerCode}&series=${seriesName}`;
      const response = await axios.get(apiUrl);
      if (response.data.status === 'success') {
        setSeriesDevices(response.data.data.devices);
        setSelectedSeries({
          customerCode: customerCode,
          seriesName: seriesName,
          customerName: customersData.find(c => c.customerCode === customerCode)?.customerName || customerCode
        });
      } else {
        setError('デバイスデータの取得に失敗しました');
      }
    } catch (error) {
      let errorMessage = 'API呼び出しに失敗しました';
      if (error.response) {
        errorMessage = `サーバーエラー: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'サーバーに接続できませんでした';
      } else {
        errorMessage = `エラー: ${error.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleBackToSeries = () => {
    setSelectedSeries(null);
    setSeriesDevices([]);
    setSelectedDevice(null);
    setDeviceHistory([]);
  };

  const handleDeviceClick = async (customData) => {
    try {
      setLoadingHistory(true);
      setError(null);
      const apiUrl = `${KSK_API_CONFIG.DEVICE_HISTORY}/?customer=${selectedSeries.customerCode}&series=${selectedSeries.seriesName}&customData=${customData}`;
      const response = await axios.get(apiUrl);
      if (response.data.status === 'success') {
        setDeviceHistory(response.data.data.history);
        setSelectedDevice({
          customData: customData,
          customerCode: selectedSeries.customerCode,
          seriesName: selectedSeries.seriesName,
          customerName: selectedSeries.customerName
        });
      } else {
        setError('デバイス履歴の取得に失敗しました');
      }
    } catch (error) {
      let errorMessage = 'API呼び出しに失敗しました';
      if (error.response) {
        errorMessage = `サーバーエラー: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'サーバーに接続できませんでした';
      } else {
        errorMessage = `エラー: ${error.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleBackToDevices = () => {
    setSelectedDevice(null);
    setDeviceHistory([]);
  };

  // 備考の保存
  const saveNote = (customData, noteText) => {
    const newNotes = { ...notes, [customData]: noteText };
    setNotes(newNotes);
    localStorage.setItem('sigfoxDeviceNotes', JSON.stringify(newNotes));
    setEditingNote(null);
  };

  // 備考の編集開始
  const startEditingNote = (customData) => {
    setEditingNote(customData);
  };

  // 備考の編集キャンセル
  const cancelEditingNote = () => {
    setEditingNote(null);
  };

  return (
    <div className={commonStyles.layout.container}>
      <div className={commonStyles.layout.content}>
        <div className={commonStyles.layout.section}>
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
              <p className="font-medium">エラー: {error}</p>
              <p className="text-sm mt-1">ダミーデータを表示しています</p>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : null}

          {/* Device Detail View */}
          {selectedDevice ? (
            <SigfoxDetailDevice
              deviceHistory={deviceHistory}
              selectedDevice={selectedDevice}
              onBackToDevices={handleBackToDevices}
              loadingHistory={loadingHistory}
              isDark={isDark}
            />
          ) : null}

          {/* Series Device List View */}
          {selectedSeries && !selectedDevice ? (
            <SigfoxSeriesDeviceList
              seriesDevices={seriesDevices}
              onDeviceClick={handleDeviceClick}
              notes={notes}
              editingNote={editingNote}
              onStartEditNote={startEditingNote}
              onSaveNote={saveNote}
              onCancelEditNote={cancelEditingNote}
              loadingDevices={loadingDevices}
              selectedSeries={selectedSeries}
              onBackToSeries={handleBackToSeries}
              isDark={isDark}
            />
          ) : null}

          {/* Customer and Series List */}
          {!loading && !selectedSeries && customersData.map((customer) => (
            <Card key={customer.customerCode} className={classNames(
              commonStyles.card.padding.default,
              "mb-6"
            )}>
              <div className="mb-4">
                <h2 className={classNames(
                  commonStyles.text.title,
                  "text-xl mb-2"
                )}>
                  {customer.customerName}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customer.series.map((series) => {
                  const getSeriesColor = (customerCode) => {
                    switch (customerCode) {
                      case 'ksk':
                        return isDark 
                          ? "bg-green-800 border-green-700 hover:bg-green-700" 
                          : "bg-green-200 border-green-400 hover:bg-green-300";
                      case 'chowa':
                        return isDark 
                          ? "bg-purple-800 border-purple-700 hover:bg-purple-700" 
                          : "bg-purple-200 border-purple-400 hover:bg-purple-300";
                      default:
                        return isDark 
                          ? "bg-gray-800 border-gray-700 hover:bg-gray-700" 
                          : "bg-gray-200 border-gray-400 hover:bg-gray-300";
                    }
                  };
                  return (
                    <div 
                      key={series.modelSeries} 
                      className={classNames(
                        "p-4 rounded-lg border cursor-pointer transition-all duration-200",
                        getSeriesColor(customer.customerCode)
                      )}
                      onClick={() => handleSeriesClick(customer.customerCode, series.modelSeries)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={classNames(
                          commonStyles.text.subtitle,
                          "font-semibold"
                        )}>
                          {series.modelSeries}
                        </h3>
                        <span className={classNames(
                          "px-2 py-1 text-xs font-semibold rounded-full",
                          isDark 
                            ? "bg-blue-800 text-blue-200" 
                            : "bg-blue-100 text-blue-800"
                        )}>
                          {series.deviceCount}台
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        登録デバイス数: {series.deviceCount}台
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SigfoxPage; 